import { mdiLifebuoy, mdiLockOutline } from "@mdi/js";
import { useEffect, useId, useState } from "react";
import { api, ORG_ADMIN_ROLE, type Profile as ProfileData } from "./api.ts";
import { supportedTimezones } from "./date.ts";
import { Icon } from "./Icon.tsx";
import { messages } from "./i18n.ts";
import { Card, PageHeader } from "./PageHeader.tsx";

/**
 * The account area.
 *
 * Left is what the person controls, right is what the system holds about them.
 * Where a regulated constraint removes a choice, it is stated rather than
 * hidden behind a control that silently does nothing: reminders for required
 * training cannot be switched off, and a name that a training record cites
 * cannot be corrected by the person the record is about.
 */

function Row({
  title,
  detail,
  trailing,
}: {
  title: string;
  detail?: string;
  trailing?: React.ReactNode;
}) {
  return (
    <div className="flex items-center gap-3.5 border-b border-border px-4 py-3 last:border-b-0">
      <div className="min-w-0 flex-1">
        <p className="text-[13px] font-semibold text-foreground">{title}</p>
        {detail ? <p className="mt-px text-[12px] text-muted-foreground">{detail}</p> : null}
      </div>
      {trailing}
    </div>
  );
}

export function Profile({
  profile,
  authProvider,
  onBack,
  onTimezoneChanged,
}: {
  profile: ProfileData;
  authProvider: string;
  onBack: () => void;
  onTimezoneChanged: (timezone: string) => void;
}) {
  const [draft, setDraft] = useState(profile.timezone);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(false);
  const [organizations, setOrganizations] = useState<{ slug: string; name: string }[]>([]);
  const timezoneId = useId();

  useEffect(() => {
    api
      .organizations()
      .then((result) => setOrganizations(result.organizations))
      .catch(() => {});
  }, []);

  const memberships =
    organizations.length > 0 ? organizations : [{ slug: profile.orgSlug, name: profile.orgName }];

  return (
    <>
      <PageHeader
        back={{ label: messages.home.title, onClick: onBack }}
        title={messages.profile.title}
      />

      <main className="grid items-start gap-4 p-4 sm:p-7 lg:grid-cols-[minmax(0,1fr)_420px]">
        <div className="flex flex-col gap-3.5">
          <Card title={messages.profile.languageAndTime}>
            <form
              onSubmit={async (event) => {
                event.preventDefault();
                setSaving(true);
                setError(false);
                setSaved(false);
                try {
                  const updated = await api.updateProfile(draft);
                  onTimezoneChanged(updated.timezone);
                  setSaved(true);
                } catch {
                  setError(true);
                } finally {
                  setSaving(false);
                }
              }}
            >
              <div className="grid gap-4 p-4 sm:grid-cols-2">
                {/*
                  The help text stays outside the label. Inside it, it joins the
                  accessible name, and the control announces as a paragraph of
                  guidance rather than as "Time zone".
                */}
                <div className="flex flex-col gap-1.5">
                  <label className="text-[12px] text-muted-foreground" htmlFor={timezoneId}>
                    {messages.profile.timezone}
                  </label>
                  <select
                    className="h-10 rounded-sm border border-input bg-card px-2.5 text-sm text-foreground"
                    id={timezoneId}
                    value={draft}
                    onChange={(event) => {
                      setDraft(event.target.value);
                      setSaved(false);
                    }}
                  >
                    {supportedTimezones(draft).map((value) => (
                      <option key={value} value={value}>
                        {value}
                      </option>
                    ))}
                  </select>
                  <p className="text-[11px] leading-normal text-muted-foreground">
                    {messages.profile.timezoneHelp}
                  </p>
                </div>

                {/*
                  Stated, not offered. There is one catalog and no locale
                  negotiation yet, so a language select here would be a control
                  that changes nothing.
                */}
                <div className="flex flex-col gap-1.5">
                  <span className="text-[12px] text-muted-foreground">
                    {messages.profile.language}
                  </span>
                  <p className="flex h-10 items-center text-sm text-foreground">
                    {messages.profile.languageValue}
                  </p>
                  <span className="text-[11px] leading-normal text-muted-foreground">
                    {messages.profile.languageHelp}
                  </span>
                </div>
              </div>

              <div className="flex items-center justify-end gap-2 border-t border-border bg-background px-4 py-3">
                {error ? (
                  <p className="mr-auto text-[12px] text-destructive" role="alert">
                    {messages.profile.timezoneSaveError}
                  </p>
                ) : null}
                {saved && !error ? (
                  <p className="mr-auto text-[12px] text-muted-foreground" role="status">
                    {messages.profile.timezoneSaved}
                  </p>
                ) : null}
                <button
                  className="btn btn-quiet"
                  disabled={saving || draft === profile.timezone}
                  type="button"
                  onClick={() => {
                    setDraft(profile.timezone);
                    setSaved(false);
                    setError(false);
                  }}
                >
                  {messages.profile.timezoneCancel}
                </button>
                <button
                  className="btn btn-primary"
                  disabled={saving || draft === profile.timezone}
                  type="submit"
                >
                  {saving ? messages.profile.timezoneSaving : messages.profile.timezoneSave}
                </button>
              </div>
            </form>
          </Card>

          <Card title={messages.profile.email}>
            <Row
              detail={messages.profile.requiredRemindersHelp}
              title={messages.profile.requiredReminders}
              trailing={
                <span className="pill bg-status-not-started-bg text-status-not-started-fg">
                  {messages.profile.alwaysOn}
                </span>
              }
            />
            <p className="px-4 py-3 text-[12px] leading-relaxed text-muted-foreground">
              {messages.profile.requiredRemindersRule}
            </p>
          </Card>

          <Card title={messages.profile.security}>
            <Row
              detail={`${profile.email} · ${messages.profile.signedInHelp}`}
              title={messages.profile.signedInWith(authProvider)}
            />
          </Card>
        </div>

        <div className="flex flex-col gap-3.5">
          <Card title={messages.profile.accountSection}>
            <div className="flex flex-col gap-3 p-4">
              <div>
                <p className="text-[15px] font-bold text-foreground">{profile.name}</p>
                <p className="text-[12px] text-muted-foreground">{profile.email}</p>
              </div>
              <p className="text-[11px] leading-normal text-muted-foreground">
                {messages.profile.identityHelp}
              </p>
              <div className="h-px bg-border" />
              <div className="flex flex-col gap-2">
                <p className="meta-label">{messages.profile.memberships}</p>
                {memberships.map((membership) => (
                  <div className="flex items-center gap-2" key={membership.slug}>
                    <span className="flex-1 truncate text-[13px] font-semibold text-foreground">
                      {membership.name}
                    </span>
                    {membership.slug === profile.orgSlug ? (
                      <span className="pill bg-status-in-progress-bg text-status-in-progress-fg">
                        {profile.roles.includes(ORG_ADMIN_ROLE)
                          ? messages.profile.roleAdministrator
                          : messages.profile.roleLearner}
                      </span>
                    ) : null}
                  </div>
                ))}
              </div>
            </div>
          </Card>

          <Card title={messages.profile.yourData}>
            <div className="flex items-start gap-3 px-4 py-3">
              <Icon className="mt-px text-muted-foreground" path={mdiLockOutline} />
              <p className="text-[12px] leading-relaxed text-muted-foreground">
                {messages.profile.retention}
              </p>
            </div>
          </Card>

          <Card title={messages.profile.legal}>
            <div className="p-2">
              <a
                className="flex h-[34px] items-center gap-2.5 rounded-md px-3 text-[13px] text-foreground hover:bg-muted"
                href={messages.support.mailto}
              >
                <Icon className="text-muted-foreground" path={mdiLifebuoy} />
                <span className="flex-1">{messages.profile.contactSupport}</span>
                <span className="text-[11px] text-muted-foreground">
                  {messages.support.address}
                </span>
              </a>
            </div>
          </Card>
        </div>
      </main>
    </>
  );
}
